"use client";

import { useEffect, useRef, type ReactNode } from "react";
import type { createHeroRoomScene } from "./hero-room-scene";

export function HeroRoom({ children }: { children: ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let disposed = false;
    let visible = false;
    let loading = false;
    let failed = false;
    let scene: Awaited<ReturnType<typeof createHeroRoomScene>> | undefined;

    const sync = async () => {
      if (
        visible &&
        !document.hidden &&
        !motion.matches &&
        !scene &&
        !loading &&
        !failed
      ) {
        loading = true;
        try {
          const { createHeroRoomScene } = await import("./hero-room-scene");
          if (disposed) return;
          const created = await createHeroRoomScene(container, canvas);
          if (disposed) {
            created.dispose();
            return;
          }
          scene = created;
        } catch {
          failed = true;
          // Keep the server-rendered artwork when WebGL or texture loading fails.
        } finally {
          loading = false;
        }
      }
      if (disposed) return;
      scene?.setActive(visible && !document.hidden && !motion.matches);
    };
    const onChange = () => {
      void sync();
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? false;
      void sync();
    });
    observer.observe(container);
    motion.addEventListener("change", onChange);
    document.addEventListener("visibilitychange", onChange);
    return () => {
      disposed = true;
      observer.disconnect();
      motion.removeEventListener("change", onChange);
      document.removeEventListener("visibilitychange", onChange);
      scene?.dispose();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className="group/hero-room pointer-events-none absolute inset-0 -z-10"
    >
      <div className="absolute inset-0 motion-safe:group-data-[animated=true]/hero-room:invisible">
        {children}
      </div>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 h-full w-full opacity-0 motion-reduce:invisible"
      />
    </div>
  );
}
